import { alpha, createTheme, darken } from '@mui/material';
import palette, { PaletteOverride } from './overrides/palette/palette';
import typography from './overrides/typography/typography';
import { drukWide } from './overrides/fontFamily';

const theme = createTheme({
  palette,

  typography,

  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 800,
      desktop: 1024,
      lg: 1200,
      bigContainer: 1500,
      xl: 1600,
    },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@font-face': [drukWide],
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.125rem',
          textTransform: 'none',
          textDecoration: 'none',
          padding: '8px 8px 24px 8px',
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          paddingLeft: 8,
          paddingRight: 8,
          colorScheme: 'light',
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          fontSize: 14,
          padding: '8px 24px',
          borderRadius: 20,
          textTransform: 'unset',
          lineHeight: 'inherit',
          boxShadow: 'none',
          '&:hover, &:active': {
            boxShadow: 'none',
          },
        },
      },

      variants: [
        {
          props: { variant: 'rounded' },
          style: {
            color: PaletteOverride.primary.contrastText,
            backgroundColor: PaletteOverride.primary.main,
            '&:hover': {
              backgroundColor: darken(PaletteOverride.primary.main, 0.1),
            },
            '&.Mui-disabled': {
              color: PaletteOverride.grey[500],
              borderColor: PaletteOverride.grey[700],
              backgroundColor: PaletteOverride.grey[700],
            },
          },
        },
        {
          props: { variant: 'outlined' },
          style: {
            color: PaletteOverride.grey[50],
            backgroundColor: 'transparent',
            border: `1px solid ${PaletteOverride.grey[50]}`,
            '&:hover': {
              border: `1px solid ${PaletteOverride.grey[50]}`,
              backgroundColor: alpha(PaletteOverride.grey[50], 0.2),
            },
            '&.Mui-disabled': {
              color: PaletteOverride.grey[700],
              borderColor: PaletteOverride.grey[700],
              backgroundColor: 'transparent',
            },
          },
        },
        {
          props: { variant: 'outlinedDark' },
          style: {
            color: PaletteOverride.grey[900],
            backgroundColor: 'transparent',
            border: `1px solid ${PaletteOverride.grey[900]}`,
            '&:hover': {
              border: `1px solid ${PaletteOverride.grey[900]}`,
              backgroundColor: alpha(PaletteOverride.grey[900], 0.2),
            },
            '&.Mui-disabled': {
              color: PaletteOverride.grey[700],
              borderColor: PaletteOverride.grey[700],
              backgroundColor: 'transparent',
            },
          },
        },
        {
          props: { variant: 'text' },
          style: {
            color: PaletteOverride.grey[50],
            backgroundColor: 'transparent',
            border: 'none',
            '&:hover': {
              backgroundColor: PaletteOverride.grey[800],
            },
            '&.Mui-disabled': {
              color: PaletteOverride.grey[700],
              backgroundColor: 'transparent',
            },
          },
        },
        {
          props: { variant: 'textDark' },
          style: {
            color: PaletteOverride.grey[900],
            backgroundColor: 'transparent',
            border: 'none',
            '&:hover': {
              backgroundColor: alpha(PaletteOverride.grey[800], 0.2),
            },
            '&.Mui-disabled': {
              color: PaletteOverride.grey[700],
              backgroundColor: 'transparent',
            },
          },
        },
        {
          props: { variant: 'plainText' },
          style: {
            color: PaletteOverride.grey[50],
            padding: 0,
            minWidth: 0,
            textTransform: 'none',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline',
              cursor: 'pointer',
            },
            '&:focus': {
              outline: 'none',
            },
            '&.Mui-disabled': {
              color: PaletteOverride.grey[700],
              backgroundColor: 'transparent',
            },
          },
        },
        {
          props: { variant: 'plainTextDark' },
          style: {
            color: PaletteOverride.grey[900],
            padding: 0,
            minWidth: 0,
            textTransform: 'none',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline',
              cursor: 'pointer',
            },
            '&:focus': {
              outline: 'none',
            },
            '&.Mui-disabled': {
              color: PaletteOverride.grey[500],
              backgroundColor: 'transparent',
            },
          },
        },
      ],
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: '4px',
        },
      },
      variants: [
        {
          props: {
            variant: 'filled',
            color: 'default',
          },
          style: {
            color: PaletteOverride.grey[50],
            backgroundColor: PaletteOverride.grey[700],
          },
        },
        {
          props: {
            variant: 'outlined',
            color: 'default',
          },
          style: {
            color: PaletteOverride.grey[400],
            borderColor: PaletteOverride.grey[400],
          },
        },
        {
          props: {
            variant: 'outlined',
            color: 'secondary',
          },
          style: {
            color: PaletteOverride.secondary.main,
            borderColor: PaletteOverride.secondary.main,
          },
        },
      ],
    },
  },
});

export default theme;
